import { useState } from "react";
import { Link } from "react-router-dom";

const categoryLinks = [
  { label: "新番速递", to: "/category/new" },
  { label: "剧场版", to: "/category/movie" },
  { label: "OVA专栏", to: "/category/ova" },
  { label: "经典典藏", to: "/category/classic" },
];

const quickLinks = [
  { label: "热门排行", to: "/ranking" },
  { label: "关于我们", to: "/about" },
  { label: "热血番推荐", to: "/category/hot-blooded" },
  { label: "治愈番推荐", to: "/category/healing" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get("contact_alt") as string | null)?.trim() || "";
    if (honeypot) {
      setStatus("success");
      form.reset();
      setEmail("");
      return;
    }
    setStatus("loading");
    setFormError("");
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, value as string);
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/da28rtjk8r5te5mqsjog", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setEmail("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "订阅失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，订阅失败，请稍后再试");
    }
  };

  return (
    <footer className="border-t border-background-300/40 bg-gradient-to-b from-background-100 to-background-200">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-secondary-400 to-primary-500 text-background-50">
                <i className="ri-bubble-chart-fill text-xl" />
              </span>
              <span className="font-heading text-xl text-foreground-950">漫番志.</span>
            </Link>
            <p className="text-sm leading-relaxed text-foreground-600">
              一站式动漫综合门户，新番、剧场版、OVA、经典分类清晰，热门排行实时更新，帮你快速定位当下值得追的作品。
            </p>
            <div className="flex gap-2">
              {["ri-weibo-line", "ri-bilibili-line", "ri-instagram-line", "ri-youtube-line"].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  aria-label={icon}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-600 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={icon} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="mb-4 text-sm font-bold text-foreground-950">番剧分类</h4>
            <ul className="space-y-3">
              {categoryLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-foreground-600 transition-colors hover:text-primary-400">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 text-sm font-bold text-foreground-950">快速导航</h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-foreground-600 transition-colors hover:text-primary-400">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 text-sm font-bold text-foreground-950">订阅更新</h4>
            <p className="mb-4 text-sm text-foreground-600">
              订阅每周新番与排行精选，第一时间收到追番提醒。
            </p>
            <form
              data-readdy-form="动漫追番订阅表单"
              onSubmit={handleSubmit}
              className="space-y-3"
            >
              <div className="flex overflow-hidden rounded-full bg-background-50 ring-1 ring-background-300/70 focus-within:ring-primary-500">
                <i className="ri-mail-line pl-4 text-sm text-foreground-500" />
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="你的邮箱"
                  className="w-full bg-transparent px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
                />
              </div>
              <input
                type="text"
                name="contact_alt"
                className="trap-guard-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-2.5 text-sm font-bold text-background-50 transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {status === "loading" ? "提交中…" : "订阅追番"}
              </button>
            </form>
            {status === "success" && (
              <p className="mt-3 flex items-center gap-1.5 text-xs text-accent-400">
                <i className="ri-checkbox-circle-fill" /> 订阅成功，感谢你的关注！
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 flex items-center gap-1.5 text-xs text-primary-400">
                <i className="ri-error-warning-fill" /> {formError}
              </p>
            )}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-background-300/40 pt-6 sm:flex-row">
          <p className="text-xs text-foreground-600">
            © {new Date().getFullYear()} 漫番志 · 用心整理每一部值得追的动漫作品
          </p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <Link to="/about" className="hover:text-primary-400">关于我们</Link>
            <Link to="/ranking" className="hover:text-primary-400">热门排行</Link>
            <span>联系：hello@manfanzhi.cn</span>
          </div>
        </div>
      </div>
    </footer>
  );
}